import { getIconProps, type IconProps } from '#types'

export interface EyeIconProps extends IconProps {
  /**
   * Whether the eye is open or closed
   * @default true
   */
  isOpen?: boolean
}

/**
 * Eye icon with animated transitions between open and closed states.
 *
 * Used for password visibility toggles and content visibility controls.
 *
 * @example
 * ```tsx
 * <button onClick={() => setShowPassword(!showPassword)}>
 *   <EyeIcon isOpen={showPassword} className="h-4 w-4" />
 * </button>
 * ```
 */
export function EyeIcon({ isOpen = true, strokeWidth = 1.5, ...props }: EyeIconProps) {
  return (
    <svg
      {...getIconProps(props)}
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={strokeWidth}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 16.75C15.3719 16.75 18.1399 14.3466 19.5342 12.835C19.8486 12.494 19.8486 12.006 19.5342 11.665C18.1399 10.1534 15.3719 7.75 12 7.75C8.62813 7.75 5.86011 10.1534 4.46582 11.665C4.15136 12.006 4.15136 12.494 4.46582 12.835C5.86011 14.3466 8.62813 16.75 12 16.75Z"
        className="transition-all duration-300 ease-in-out"
      />

      <circle
        cx="12"
        cy="12.25"
        r="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="transition-all duration-300 ease-in-out"
        style={{
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'scale(1)' : 'scale(0.5)',
          transformOrigin: 'center',
        }}
      />

      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.25C10.75 14 13.25 14 15 12.25"
        className="transition-all duration-300 ease-in-out"
        style={{
          opacity: isOpen ? 0 : 1,
          transform: isOpen ? 'scale(0.8)' : 'scale(1)',
          transformOrigin: 'center',
        }}
      />
    </svg>
  )
}
